/**
 * AudioReplyContext - unifies the user's "speak the assistant's
 * replies aloud" preference AND the live audio playback state.
 *
 * Two facets, deliberately in one context:
 *
 *   PREFERENCE (`enabled`)
 *     User toggle. Persisted to localStorage. Default OFF -
 *     speaking aloud is opt-in. The Composer reads this to decide
 *     whether to set `audio_requested: true` on typed-question
 *     asks. Voice-driven asks force `audio_requested: true`
 *     regardless of this flag (see Composer.handleVoiceTranscript).
 *
 *   PLAYBACK (`isPlaying` / `playingFor`)
 *     Live status. `isPlaying` is true while a reply is being
 *     read out. `playingFor` is the timestamp of the assistant
 *     turn whose audio is currently playing - Message renders a
 *     per-turn stop button when this matches its own ts.
 *
 * The toggle's visual checked state is `(enabled || isPlaying)` -
 * so a voice-driven temporary playback shows the toggle as "ON"
 * for that moment, and clicks during playback stop the audio
 * rather than just flipping the preference. Once the audio ends
 * the toggle reverts to the user's stored preference (OFF for
 * most users).
 *
 * Why one context and not two: the toggle's click semantics
 * depend on whether audio is currently playing, so it'd need
 * both anyway. Keeping them together avoids prop-drilling and a
 * second context boundary.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'platform-ui:audio-replies';

interface AudioReplyContextValue {
  /** User preference, persisted across sessions. Default false. */
  enabled: boolean;
  setEnabled: (next: boolean) => void;
  toggle: () => void;
  /** True while an assistant audio reply is actively playing. */
  isPlaying: boolean;
  /** Turn timestamp currently being read (matches
   *  ConversationTurn.timestamp). null when nothing is playing. */
  playingFor: string | null;
  /** Start playback of `url`. If another clip is playing, stop
   *  it first (no overlapping audio - sounds like a malfunction).
   *  Optional `forTimestamp` lets a Message render its per-turn
   *  stop button when this matches its own ts. */
  playUrl: (url: string, forTimestamp?: string) => void;
  /** Stop any currently-playing clip. No-op when nothing is
   *  playing. Called by the per-message stop button + by the
   *  composer when a new message is sent (don't bleed previous
   *  audio into a fresh question). */
  stop: () => void;
}

const AudioReplyContext = createContext<AudioReplyContextValue | null>(null);

function readInitial(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function AudioReplyProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabled] = useState<boolean>(readInitial);
  const [playingFor, setPlayingFor] = useState<string | null>(null);
  // Live audio element kept in a ref so stop() can pause it from
  // anywhere. We attach to a singleton hidden <audio> mounted
  // alongside the provider; one element is enough since we only
  // ever play one reply at a time.
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Persist preference. Pure mirror of state → storage.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(enabled));
    } catch {
      /* private mode - choice just won't persist. */
    }
  }, [enabled]);

  const stop = useCallback(() => {
    const el = audioRef.current;
    if (el) {
      try {
        el.pause();
        el.currentTime = 0;
        // Drop the src so a subsequent play() is a deliberate
        // reload, not a resume of the previous URL.
        el.removeAttribute('src');
        el.load();
      } catch {
        /* element in a bad state - clearing playingFor below is
         * what the UI cares about. */
      }
    }
    setPlayingFor(null);
  }, []);

  const playUrl = useCallback(
    (url: string, forTimestamp?: string) => {
      const el = audioRef.current;
      if (!el || !url) return;
      // Stop any current playback first - overlapping voices
      // sound like a bug. setPlayingFor below sets the new
      // tracker before play() resolves.
      try {
        el.pause();
        el.currentTime = 0;
      } catch {
        /* ignore */
      }
      el.src = url;
      // forTimestamp is opaque to us - caller is responsible for
      // matching it against turn.timestamp when rendering the
      // stop button. Use a stable sentinel for the imperative
      // path (SendContext's onSuccess) when no timestamp is
      // known yet - the toggle's visual still flips, the
      // per-turn button doesn't show.
      setPlayingFor(forTimestamp || '__pending__');
      void el.play().catch(() => {
        // Browser blocked autoplay (no user gesture) or some
        // other transport hiccup. Don't leave the toggle
        // showing "playing" forever in that case.
        setPlayingFor(null);
      });
    },
    [],
  );

  // <audio> event listeners - clear playingFor when the clip ends
  // naturally, errors out, or is paused externally. Set up once
  // on mount.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onEnded = () => setPlayingFor(null);
    const onError = () => setPlayingFor(null);
    el.addEventListener('ended', onEnded);
    el.addEventListener('error', onError);
    return () => {
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('error', onError);
    };
  }, []);

  const value = useMemo<AudioReplyContextValue>(
    () => ({
      enabled,
      setEnabled,
      toggle: () => setEnabled((v) => !v),
      isPlaying: playingFor !== null,
      playingFor,
      playUrl,
      stop,
    }),
    [enabled, playingFor, playUrl, stop],
  );

  return (
    <AudioReplyContext.Provider value={value}>
      {children}
      {/* Singleton playback element. Hidden; the toggle and
          per-message buttons surface playback state in the UI. */}
      <audio
        ref={audioRef}
        style={{ display: 'none' }}
        aria-hidden
      />
    </AudioReplyContext.Provider>
  );
}

export function useAudioReply(): AudioReplyContextValue {
  const ctx = useContext(AudioReplyContext);
  if (!ctx) {
    throw new Error('useAudioReply must be used inside <AudioReplyProvider>');
  }
  return ctx;
}
